/**
 * Implementation factory type
 */
export type ImplementationFactory<T> = () => T | Promise<T>;

/**
 * Public API types with improved get method
 */
export interface Registry<T, Names extends string> {
  register: (
    name: Names,
    factory: ImplementationFactory<T>,
  ) => Registry<T, Names>;

  // Overloaded get method that infers return types based on input.
  get: {
    <K extends Names>(name: K): Promise<T>;
    <K extends [Names, ...Names[]]>(
      ...names: K
    ): Promise<{ [P in keyof K]: T }>;
  };
}

/**
 * @name createRegistry
 * @description Creates a new registry instance with the provided implementations.
 * @returns A new registry instance.
 */
export function createRegistry<T, Names extends string = string>(): Registry<
  T,
  Names
> {
  const implementations = new Map<Names, ImplementationFactory<T>>();

  const registry: Registry<T, Names> = {
    register(name, factory) {
      implementations.set(name, factory);
      return registry;
    },

    // Updated get method overload that supports tuple inference
    get: (async (...names: Names[]) => {
      if (names.length === 1) {
        return await getImplementation(names[0]!);
      }

      return await Promise.all(names.map((name) => getImplementation(name)));
    }) as Registry<T, Names>['get'],
  };

  async function getImplementation(name: Names) {
    const factory = implementations.get(name);

    if (!factory) {
      throw new Error(`Implementation "${name}" not found`);
    }

    const implementation = await factory();

    if (!implementation) {
      throw new Error(`Implementation "${name}" is not available`);
    }

    return implementation;
  }

  return registry;
}
